import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, CalendarClock, Plus, Repeat } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { clientPortalService } from '../../services/clientPortal.service';
import { recurringOrdersService } from '../../services/recurringOrders.service';
import { getClientPermissions } from '../../lib/clientPermissions';
import { ScheduleFormDialog } from './recurring/ScheduleFormDialog';
import { OccurrenceReviewDialog } from './recurring/OccurrenceReviewDialog';
import { ScheduleCard } from './recurring/ScheduleCard';
import { StatusBadge, itemsSummary, prettyDate } from './recurring/recurringShared';

const RecurringOrders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const canPlaceOrders = getClientPermissions().can_place_orders;

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [occurrences, setOccurrences] = useState([]);
  const [locations, setLocations] = useState([]);
  const [allowedDays, setAllowedDays] = useState([]);

  const [formOpen, setFormOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [reviewId, setReviewId] = useState(null);

  const load = async () => {
    try {
      const [tpls, occs] = await Promise.all([
        recurringOrdersService.listTemplates(),
        recurringOrdersService.listOccurrences(),
      ]);
      setTemplates(tpls);
      setOccurrences(occs);
    } catch (e) {
      toast.error('Failed to load recurring orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    clientPortalService.getProfile()
      .then((p) => {
        setLocations(p.locations || []);
        setAllowedDays(p.account?.preferences?.allowed_delivery_days || []);
      })
      .catch(() => {});
  }, []);

  // Checkout entry point: /client/recurring-orders?prefill=<order_id>
  useEffect(() => {
    const orderId = searchParams.get('prefill');
    if (!orderId || !canPlaceOrders) return;
    clientPortalService.getOrders()
      .then((orders) => {
        const order = orders.find((o) => o.id === orderId);
        if (!order) return;
        setPrefill({
          items: order.items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
          location_id: order.location_id || null,
        });
        setEditTemplate(null);
        setFormOpen(true);
      })
      .catch(() => {})
      .finally(() => setSearchParams({}, { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openCreate = () => {
    setEditTemplate(null);
    setPrefill(null);
    setFormOpen(true);
  };

  const openEdit = (t) => {
    setEditTemplate(t);
    setPrefill(null);
    setFormOpen(true);
  };

  const doAction = async (fn, okMsg) => {
    try {
      await fn();
      toast.success(okMsg);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const handleSkip = (occ) =>
    doAction(() => recurringOrdersService.skipOccurrence(occ.id), 'Date skipped — your schedule stays active');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="recurring-loading">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recurring-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Recurring Orders</h1>
          <p className="text-sm text-gray-600 mt-1">
            Standing orders that you confirm, edit, or skip before each delivery
          </p>
        </div>
        {canPlaceOrders && (
          <Button className="bg-amber-500 hover:bg-amber-600" onClick={openCreate} data-testid="create-schedule-btn">
            <Plus className="h-4 w-4 mr-2" /> New schedule
          </Button>
        )}
      </div>

      {!canPlaceOrders && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="recurring-readonly-notice">
          You have view-only access — ask your account owner to make changes to recurring orders.
        </div>
      )}

      {/* Needs your attention */}
      {occurrences.length > 0 && (
        <div data-testid="needs-attention-section">
          <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-amber-500" />
            Needs your attention
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {occurrences.map((occ) => (
              <Card key={occ.id} data-testid={`occurrence-card-${occ.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900">{prettyDate(occ.delivery_date)}</p>
                        <StatusBadge status={occ.status} testId={`occurrence-status-${occ.id}`} />
                      </div>
                      <p className="text-xs text-amber-700 font-medium mt-0.5" data-testid={`occurrence-deadline-${occ.id}`}>
                        Act {occ.deadline_label}
                      </p>
                    </div>
                    <p className="text-sm font-bold text-gray-900 shrink-0">${(occ.estimated_total || 0).toFixed(2)}</p>
                  </div>
                  <p className="text-sm text-gray-700 mt-2 line-clamp-2" data-testid={`occurrence-items-${occ.id}`}>
                    {itemsSummary(occ.resolved_items)}
                  </p>
                  {occ.status === 'needs_attention' && (
                    <div className="mt-2 flex items-start gap-1.5 text-xs text-red-600" data-testid={`occurrence-changed-${occ.id}`}>
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      <span>
                        Menu changed: {occ.resolved_items.filter((i) => !i.available).map((i) => i.product_name).join(', ')} unavailable — review to swap.
                      </span>
                    </div>
                  )}
                  {occ.template_summary && (
                    <p className="text-xs text-gray-400 mt-1.5">
                      {occ.template_summary.cadence_label} schedule
                      {occ.template_summary.location_name ? ` · ${occ.template_summary.location_name}` : ''}
                    </p>
                  )}
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      className="bg-amber-500 hover:bg-amber-600"
                      onClick={() => setReviewId(occ.id)}
                      data-testid={`occurrence-review-btn-${occ.id}`}
                    >
                      Review
                    </Button>
                    {canPlaceOrders && (
                      <Button size="sm" variant="outline" onClick={() => handleSkip(occ)} data-testid={`occurrence-skip-btn-${occ.id}`}>
                        Skip this date
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* My schedules */}
      <div data-testid="schedules-section">
        <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Repeat className="h-5 w-5 text-amber-500" />
          My schedules
        </h2>
        {templates.length === 0 ? (
          <Card>
            <CardContent className="p-10 text-center" data-testid="no-schedules">
              <Repeat className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">No recurring schedules yet</p>
              <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
                Set up a daily or weekly standing order. We'll line up each delivery date for your
                review — nothing is ordered until you confirm, or your no-action policy kicks in.
              </p>
              {canPlaceOrders && (
                <Button className="bg-amber-500 hover:bg-amber-600 mt-4" onClick={openCreate} data-testid="empty-create-schedule-btn">
                  <Plus className="h-4 w-4 mr-2" /> Create your first schedule
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {templates.map((t) => (
              <ScheduleCard
                key={t.id}
                template={t}
                canPlaceOrders={canPlaceOrders}
                onEdit={openEdit}
                onPause={(tpl) => doAction(() => recurringOrdersService.pauseTemplate(tpl.id), 'Schedule paused')}
                onResume={(tpl) => doAction(() => recurringOrdersService.resumeTemplate(tpl.id), 'Schedule resumed')}
                onCancel={(tpl) => doAction(() => recurringOrdersService.cancelTemplate(tpl.id), 'Schedule cancelled')}
              />
            ))}
          </div>
        )}
      </div>

      <ScheduleFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        template={editTemplate}
        prefill={prefill}
        locations={locations}
        allowedDays={allowedDays}
        onSaved={load}
      />
      <OccurrenceReviewDialog
        open={!!reviewId}
        onOpenChange={(v) => !v && setReviewId(null)}
        occurrenceId={reviewId}
        canPlaceOrders={canPlaceOrders}
        onDone={load}
      />
    </div>
  );
};

export default RecurringOrders;
